import type { StrictPaddingStyle } from '../util/types';

import { SelectionIcon } from '../CheckChip/SelectionIcon';
import { Label } from '../Label';
import { useTheme } from '../ThemeProvider';
import { useAnimatedNative } from '../util/useAnimatedNative';

import React, { useEffect, useMemo, useRef } from 'react';
import {
    Animated,
    Easing,
    Platform,
    Pressable,
    type StyleProp,
    StyleSheet,
    useWindowDimensions,
    View,
    type ViewStyle,
} from 'react-native';

// --- Base constants for dimensions and animation ---
const BASE_SWITCH_WIDTH = 51;
const BASE_SWITCH_HEIGHT = 31;
const BASE_THUMB_SIZE = 24;
const BASE_ICON_SIZE = 20;
// ---

export type SwitchProps = {
    label?: string;
    value: boolean;
    onValueChange: (value: boolean) => void;
    disabled?: boolean;
    switchTestID?: string;
    containerStyle?: StyleProp<ViewStyle>;

    /**
     * Defines the padding style overrides.
     */
    sx?: StrictPaddingStyle;

    /**
     * Accessibility hint to describe what the switch controls
     */
    accessibilityHint?: string;
};

export const Switch = ({
    label,
    value,
    onValueChange,
    disabled = false,
    switchTestID,
    containerStyle,
    sx,
    accessibilityHint,
}: SwitchProps) => {
    const theme = useTheme();
    const isWeb = Platform.OS === 'web';
    const { fontScale } = useWindowDimensions();
    const animatedValue = useRef(new Animated.Value(value ? 1 : 0)).current;
    const rotationAnim = useRef(new Animated.Value(value ? 1 : 0)).current;
    const switchRef = useRef<View>(null);
    const ANIMATION_DURATION = theme.durations.quick;

    // Calculate dimensions based on font scale for Dynamic Type support
    const dimensions = useMemo(() => {
        const switchWidth = BASE_SWITCH_WIDTH * fontScale;
        const switchHeight = BASE_SWITCH_HEIGHT * fontScale;
        const thumbSize = BASE_THUMB_SIZE * fontScale;
        const padding = (switchHeight - thumbSize) / 2;
        const thumbOffPosition = padding;
        const thumbOnPosition = switchWidth - thumbSize - padding;
        const iconSize = BASE_ICON_SIZE * fontScale;
        const strokeWidth = 2 * fontScale;

        return {
            switchWidth,
            switchHeight,
            thumbSize,
            thumbOffPosition,
            thumbOnPosition,
            iconSize,
            strokeWidth,
        };
    }, [fontScale]);

    const useNativeDriver = useAnimatedNative();

    useEffect(() => {
        const animation = Animated.parallel([
            Animated.timing(animatedValue, {
                toValue: value ? 1 : 0,
                duration: ANIMATION_DURATION,
                easing: Easing.inOut(Easing.ease),
                useNativeDriver: useNativeDriver,
            }),
            Animated.timing(rotationAnim, {
                toValue: value ? 1 : 0,
                duration: ANIMATION_DURATION,
                useNativeDriver: useNativeDriver,
            }),
        ]);

        animation.start();

        return () => {
            animation.stop();
        };
    }, [value, animatedValue, rotationAnim, ANIMATION_DURATION, useNativeDriver]);

    const animatedRotation = rotationAnim.interpolate({
        inputRange: [0, 1],
        outputRange: ['-45deg', '0deg'],
    });

    const trackColorOn = theme.colors.backgroundActionInverse;
    const trackColorOff = theme.colors.backgroundActionWeaker;
    const thumbColor = theme.colors.backgroundActionInverse;
    const thumbPosition = animatedValue.interpolate({
        inputRange: [0, 1],
        outputRange: [dimensions.thumbOffPosition, dimensions.thumbOnPosition],
    });

    const trackBackgroundColor = animatedValue.interpolate({
        inputRange: [0, 1],
        outputRange: [trackColorOff, trackColorOn],
    });

    const animatedTrackStyle = {
        backgroundColor: trackBackgroundColor,
    };

    const animatedThumbStyle = {
        transform: [{ translateX: thumbPosition }],
        backgroundColor: thumbColor,
    };

    const handlePress = () => {
        if (!disabled) {
            onValueChange(!value);
        }
    };

    const handleLabelPress = () => {
        handlePress();
        if (isWeb && switchRef.current) {
            (switchRef.current as unknown as HTMLElement).focus();
        }
    };

    const styles = StyleSheet.create({
        container: {
            flexDirection: 'row',
            alignItems: 'center',
        },
        pressableContainer: {
            marginRight: 10,
            userSelect: 'none',
        },
        labelPressable: {
            cursor: 'pointer',
            userSelect: 'none',
            flexShrink: 1,
        },
        track: {
            borderRadius: theme.borderRadius.full,
            justifyContent: 'center',
            borderWidth: 1,
            borderColor: theme.colors.borderAction,
        },
        thumb: {
            borderRadius: theme.borderRadius.full,
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
        },
        iconWrapperChecked: {
            width: '100%',
            height: '100%',
            borderRadius: theme.borderRadius.full,
            alignItems: 'center',
            justifyContent: 'center',
        },
    });

    return (
        <View style={[styles.container, containerStyle, sx]}>
            <Pressable
                ref={switchRef}
                onPress={handlePress}
                style={[styles.pressableContainer, { opacity: disabled ? 0.4 : 1 }]}
                disabled={disabled}
                accessibilityRole="switch"
                accessibilityState={{ checked: value, disabled }}
                accessibilityLabel={label}
                accessibilityHint={accessibilityHint}
                accessibilityValue={{ text: value ? 'On' : 'Off' }}
                accessibilityLiveRegion="polite"
                {...(isWeb && {
                    'aria-checked': value,
                })}
                testID={switchTestID}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
                <Animated.View
                    style={[
                        styles.track,
                        animatedTrackStyle,
                        {
                            width: dimensions.switchWidth,
                            height: dimensions.switchHeight,
                        },
                    ]}
                >
                    <Animated.View
                        style={[
                            styles.thumb,
                            animatedThumbStyle,
                            {
                                width: dimensions.thumbSize,
                                height: dimensions.thumbSize,
                            },
                        ]}
                    >
                        <Animated.View
                            style={[
                                styles.iconWrapperChecked,
                                {
                                    backgroundColor: value
                                        ? theme.colors.foregroundInverse
                                        : theme.colors.foregroundStrong,
                                },
                                { transform: [{ rotate: animatedRotation }] },
                            ]}
                        >
                            <SelectionIcon
                                testID={`${switchTestID}_icon`}
                                checked={value}
                                strokeColor={
                                    value
                                        ? theme.colors.foregroundStrong
                                        : theme.colors.foregroundInverse
                                }
                                size={dimensions.iconSize}
                                strokeWidth={dimensions.strokeWidth}
                            />
                        </Animated.View>
                    </Animated.View>
                </Animated.View>
            </Pressable>
            {label && (
                <Pressable
                    onPress={handleLabelPress}
                    style={styles.labelPressable}
                    accessible={false}
                    disabled={isWeb ? disabled : undefined}
                >
                    <Label text={label} />
                </Pressable>
            )}
        </View>
    );
};
